import React, { useState, useEffect, useRef } from "react";
import { Card, Badge, Row, Col, Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMusic,
  faGuitar,
  faClock,
  faSpinner,
  faKey,
  faTachometerAlt,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import { useAudioWithTime } from "../context/AudioContext";
import "./components.css";

const BeatsChordVisualization = ({ project }) => {
  const { currentTime, duration, isPlaying, currentProjectId } = useAudioWithTime();

  const [beatsData, setBeatsData] = useState(null);
  const [chordsData, setChordsData] = useState(null);
  const [beatsLoading, setBeatsLoading] = useState(false);
  const [chordsLoading, setChordsLoading] = useState(false);
  const [beatsError, setBeatsError] = useState(null);
  const [chordsError, setChordsError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // Get beats/chords asset from project
  const beatsChordAsset = project?.audio_assets?.find(
    (asset) => asset.type === "BEATS_CHORDS" && asset.status === "READY"
  );

  // Helper function to determine if data is a URL or direct data
  const isDataUrl = (data) => {
    return (
      typeof data === "string" &&
      (data.startsWith("http") || data.startsWith("/"))
    );
  };

  // Fetch beats data from URL or use direct data
  const processBeatsData = async (beatsData) => {
    if (!beatsData) return;

    setBeatsLoading(true);
    setBeatsError(null);

    try {
      let data;
      if (isDataUrl(beatsData)) {
        // Data is a URL, fetch it
        const response = await fetch(beatsData);
        if (!response.ok) {
          throw new Error(`Failed to fetch beats data: ${response.status}`);
        }
        data = await response.json();
      } else {
        // Data is already an object/array, use it directly
        data = beatsData;
      }

      // Validate data structure
      if (Array.isArray(data)) {
        // Ensure each beat has required properties
        const validBeats = data.filter(
          (beat) => beat && typeof beat.time === "number" && !isNaN(beat.time)
        );
        setBeatsData(validBeats);
        console.log(
          `Processed ${validBeats.length} valid beats out of ${data.length} total`
        );
      } else {
        console.warn("Beats data is not an array:", data);
        setBeatsError("Invalid beats data format - expected array");
      }
    } catch (error) {
      console.error("Error processing beats data:", error);
      setBeatsError(error.message);
    } finally {
      setBeatsLoading(false);
    }
  };

  // Fetch chords data from URL or use direct data
  const processChordsData = async (chordsData) => {
    if (!chordsData) return;

    setChordsLoading(true);
    setChordsError(null);

    try {
      let data;
      if (isDataUrl(chordsData)) {
        // Data is a URL, fetch it
        const response = await fetch(chordsData);
        if (!response.ok) {
          throw new Error(`Failed to fetch chords data: ${response.status}`);
        }
        data = await response.json();
      } else {
        // Data is already an object/array, use it directly
        data = chordsData;
      }

      // Validate data structure
      if (Array.isArray(data)) {
        // Ensure each chord has required properties
        const validChords = data.filter(
          (chord) =>
            chord &&
            typeof chord.start === "number" &&
            !isNaN(chord.start) &&
            typeof chord.end === "number" &&
            !isNaN(chord.end) &&
            chord.start < chord.end
        );
        setChordsData(validChords);
        console.log(
          `Processed ${validChords.length} valid chords out of ${data.length} total`
        );
      } else {
        console.warn("Chords data is not an array:", data);
        setChordsError("Invalid chords data format - expected array");
      }
    } catch (error) {
      console.error("Error processing chords data:", error);
      setChordsError(error.message);
    } finally {
      setChordsLoading(false);
    }
  };

  // Process data when component mounts or asset changes
  useEffect(() => {
    if (beatsChordAsset) {
      // Extract metadata
      setMetadata(beatsChordAsset.metadata);

      // Process beats and chords data
      if (beatsChordAsset.beats) {
        processBeatsData(beatsChordAsset.beats);
      }

      if (beatsChordAsset.chords) {
        processChordsData(beatsChordAsset.chords);
      }
    } else {
      // Reset state when no asset
      setBeatsData(null);
      setChordsData(null);
      setMetadata(null);
    }
  }, [beatsChordAsset]);

  // Get current beat with tolerance - improved logic
  const getCurrentBeat = () => {
    if (
      !beatsData ||
      !Array.isArray(beatsData) ||
      currentTime === undefined ||
      currentTime === null
    )
      return null;

    // Only highlight if this is the current project playing - ensure string comparison
    if (String(currentProjectId) !== String(project?.id)) return null;

    // Find beat within tolerance - use larger tolerance for better detection
    return beatsData.find((beat) => {
      if (beat.time !== undefined && beat.time !== null) {
        return Math.abs(beat.time - currentTime) <= 0.3; // Increased tolerance
      }
      return false;
    });
  };

  // Get current chord - improved logic
  const getCurrentChord = () => {
    if (
      !chordsData ||
      !Array.isArray(chordsData) ||
      currentTime === undefined ||
      currentTime === null
    )
      return null;

    // Only highlight if this is the current project playing - ensure string comparison
    if (String(currentProjectId) !== String(project?.id)) return null;

    return chordsData.find((chord) => {
      if (
        chord.start !== undefined &&
        chord.end !== undefined &&
        chord.start !== null &&
        chord.end !== null
      ) {
        return currentTime >= chord.start && currentTime <= chord.end;
      }
      return false;
    });
  };

  const currentBeat = getCurrentBeat();
  const currentChord = getCurrentChord();

  // Format time display
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get chord display name - prioritize pop notation, then fall back to others
  const getChordDisplayName = (chordData) => {
    if (!chordData) return "N";
    if (chordData.chord_simple_pop && chordData.chord_simple_pop !== "N") {
      return chordData.chord_simple_pop;
    }
    if (chordData.chord_basic_pop && chordData.chord_basic_pop !== "N") {
      return chordData.chord_basic_pop;
    }
    if (chordData.chord_complex_pop && chordData.chord_complex_pop !== "N") {
      return chordData.chord_complex_pop;
    }
    if (chordData.chord_majmin && chordData.chord_majmin !== "N") {
      return chordData.chord_majmin;
    }
    return "N"; // No chord
  };

  // Get chord color based on chord name
  const getChordColor = (chordName) => {
    if (!chordName || chordName === "N") return "#6c757d"; // Gray for no chord

    const colors = {
      C: "#FF6B6B",
      "C#": "#FF8E53",
      D: "#FF6B9D",
      "D#": "#C44569",
      E: "#F8B500",
      F: "#18A558",
      "F#": "#2C5F41",
      G: "#3742FA",
      "G#": "#5F27CD",
      A: "#A55EEA",
      "A#": "#FF3838",
      B: "#FF9F43",
    };

    // Extract the root note (handle formats like "Am", "A:min", "A-", etc.)
    const baseChord = chordName.split(/[^A-G#]/)[0];
    return colors[baseChord] || "#8395A7";
  };

  // Debug logging - moved after function definitions
  useEffect(() => {
    if (beatsData || chordsData) {
      console.log("=== BeatsChordVisualization Debug ===");
      console.log("Current time:", currentTime);
      console.log("Project ID:", project?.id);
      console.log("Current project ID:", currentProjectId);
      console.log("Is playing:", isPlaying);
      console.log("Beats data length:", beatsData?.length || 0);
      console.log("Chords data length:", chordsData?.length || 0);
      console.log("First beat time:", beatsData?.[0]?.time);
      console.log(
        "First chord range:",
        chordsData?.[0] ? `${chordsData[0].start}-${chordsData[0].end}` : "none"
      );

      if (currentBeat) {
        console.log("Current beat:", currentBeat);
        console.log(
          "Beat time diff:",
          Math.abs(currentBeat.time - currentTime)
        );
      } else {
        console.log("No current beat found");
        if (beatsData?.length > 0) {
          const closestBeat = beatsData.reduce((closest, beat) => {
            const closestDiff = Math.abs(closest.time - currentTime);
            const beatDiff = Math.abs(beat.time - currentTime);
            return beatDiff < closestDiff ? beat : closest;
          });
          console.log(
            "Closest beat:",
            closestBeat,
            "diff:",
            Math.abs(closestBeat.time - currentTime)
          );
        }
      }

      if (currentChord) {
        console.log("Current chord:", currentChord);
      } else {
        console.log("No current chord found");
        if (chordsData?.length > 0) {
          const chordAtTime = chordsData.find(
            (chord) => currentTime >= chord.start && currentTime <= chord.end
          );
          console.log(
            "Chord check:",
            chordAtTime ? "found" : "none",
            "time:",
            currentTime
          );
        }
      }
      console.log("===================================");
    }
  }, [
    beatsData,
    chordsData,
    currentTime,
    isPlaying,
    currentProjectId,
    project?.id,
    currentBeat,
    currentChord,
  ]);

  // Render metadata section
  const renderMetadata = () => {
    if (!metadata) return null;

    const bpm = metadata.bpm || metadata.result?.bpm;
    const rootKey = metadata.root_key || metadata.result?.["root key"];

    return (
      <Card
        className="mb-3"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Card.Body className="py-3">
          <h6 className="mb-3" style={{ color: "#d9d9d9" }}>
            <FontAwesomeIcon
              icon={faMusic}
              className="me-2"
              style={{ color: "var(--purple-accent)" }}
            />
            Music Analysis Metadata
          </h6>
          <Row>
            {bpm && (
              <Col md={6} className="mb-2">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faTachometerAlt}
                    className="me-2"
                    style={{ color: "var(--purple-accent)" }}
                  />
                  <span style={{ color: "#d9d9d9" }}>BPM: </span>
                  <Badge bg="primary" className="ms-2">
                    {bpm}
                  </Badge>
                </div>
              </Col>
            )}
            {rootKey && (
              <Col md={6} className="mb-2">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faKey}
                    className="me-2"
                    style={{ color: "var(--purple-accent)" }}
                  />
                  <span style={{ color: "#d9d9d9" }}>Root Key: </span>
                  <Badge bg="secondary" className="ms-2">
                    {rootKey}
                  </Badge>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Remove the time window controls - we don't need them anymore
  const renderTimeWindowControls = () => {
    return null; // Remove this functionality
  };

  // Enhanced progress bar with click-to-seek functionality
  const renderProgressBar = () => {
    if (!duration || duration === 0) return null;

    const progressPercentage = (currentTime / duration) * 100;

    const handleProgressClick = (e) => {
      // This would require audio element ref to implement seeking
      // For now, just show the interaction
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickPercentage = (clickX / rect.width) * 100;
      const newTime = (clickPercentage / 100) * duration;
      console.log(`Would seek to: ${formatTime(newTime)}`);
    };

    return (
      <div className="progress-container mb-3" style={{ position: "relative" }}>
        <div
          className="progress"
          style={{
            height: "6px",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "3px",
            cursor: "pointer",
          }}
          onClick={handleProgressClick}
        >
          <div
            className="progress-bar"
            style={{
              width: `${progressPercentage}%`,
              backgroundColor: "var(--purple-accent)",
              transition: "width 0.1s ease",
            }}
          />
        </div>
        <div className="d-flex justify-content-between mt-1">
          <small className="text-muted">{formatTime(currentTime)}</small>
          <small className="text-muted">{formatTime(duration)}</small>
        </div>

        {/* Add timeline markers for better navigation */}
        <div
          className="d-flex justify-content-between mt-1"
          style={{ fontSize: "var(--font-xs)" }}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((position) => (
            <small key={position} className="text-muted">
              {formatTime(duration * position)}
            </small>
          ))}
        </div>
      </div>
    );
  };

  // Enhanced render beats timeline - show ALL beats as actual bars scaled by beat number with purple color scheme
  const renderBeatsTimeline = () => {
    if (beatsLoading) {
      return (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <p className="small text-muted mt-2">Loading beats...</p>
        </div>
      );
    }

    if (beatsError) {
      return (
        <Alert variant="warning" className="small">
          {beatsError}
        </Alert>
      );
    }

    if (!beatsData || !Array.isArray(beatsData) || beatsData.length === 0) {
      return (
        <div className="text-center py-3">
          <FontAwesomeIcon icon={faMusic} className="text-muted mb-2" />
          <p className="small text-muted">No beats data available</p>
        </div>
      );
    }

    // Sort all beats by time
    const sortedBeats = [...beatsData].sort((a, b) => a.time - b.time);

    // Calculate bar numbers if not provided
    const beatsWithBars = sortedBeats.map((beat, index) => {
      // Check if beat has bar information directly
      if (beat.bar !== undefined) {
        return { ...beat, barNumber: beat.bar };
      }
      
      // If beat has beatNum, calculate bar (assuming 4 beats per bar)
      if (beat.beatNum !== undefined) {
        return { ...beat, barNumber: Math.ceil(beat.beatNum / 4) };
      }
      
      // Fall back to calculating from index (assuming 4 beats per bar)
      return { ...beat, barNumber: Math.ceil((index + 1) / 4) };
    });

    // Check if this project is currently playing
    const isCurrentProject = String(currentProjectId) === String(project?.id);

    // Find max beat number for scaling
    const maxBeatNum = Math.max(...beatsWithBars.map(beat => beat.beatNum || beat.barNumber || 1));
    const baseHeight = 20; // Minimum bar height
    const maxHeight = 80; // Maximum bar height

    return (
      <div
        className="beats-timeline"
        style={{ overflowX: "auto", padding: "10px 0", height: "150px" }}
      >
        <div
          className="d-flex align-items-end gap-1"
          style={{ minWidth: "max-content", paddingBottom: "10px", height: "120px" }}
        >
          {beatsWithBars.map((beat, index) => {
            // Use improved detection logic - match with getCurrentBeat logic
            const isCurrent =
              isCurrentProject &&
              currentTime !== undefined &&
              currentTime !== null &&
              beat.time !== undefined &&
              beat.time !== null &&
              Math.abs(beat.time - currentTime) <= 0.3;
            const isPast =
              isCurrentProject &&
              currentTime !== undefined &&
              currentTime !== null &&
              beat.time !== undefined &&
              beat.time !== null &&
              beat.time < currentTime - 0.3;
            const isUpcoming =
              isCurrentProject &&
              currentTime !== undefined &&
              currentTime !== null &&
              beat.time !== undefined &&
              beat.time !== null &&
              beat.time > currentTime + 0.3;

            // Calculate bar height based on beat number
            const beatNumber = beat.beatNum || beat.barNumber || (index + 1);
            const barHeight = baseHeight + ((beatNumber / maxBeatNum) * (maxHeight - baseHeight));

            return (
              <div
                key={index}
                className={`beat-bar d-flex flex-column align-items-center ${
                  isCurrent ? "current" : ""
                }`}
                style={{
                  minWidth: "16px",
                  position: "relative",
                  margin: "0 1px",
                }}
              >
                {/* The actual bar */}
                <div
                  style={{
                    width: "12px",
                    height: `${barHeight}px`,
                    backgroundColor: isCurrent
                      ? "rgba(139, 69, 208, 0.9)" // Current playing - bright purple
                      : isPast
                      ? "rgba(139, 69, 208, 0.6)" // Past beats - medium purple
                      : isUpcoming || !isCurrentProject
                      ? "rgba(139, 69, 208, 0.2)" // Upcoming - subtle purple
                      : "rgba(139, 69, 208, 0.2)",
                    borderRadius: "2px",
                    border: isCurrent
                      ? "1px solid rgba(139, 69, 208, 1)" // Current border - solid purple
                      : isPast
                      ? "1px solid rgba(139, 69, 208, 0.8)" // Past border - strong purple
                      : "1px solid rgba(139, 69, 208, 0.3)", // Upcoming border - subtle purple
                    transition: "all 0.2s ease",
                    transform: isCurrent ? "scaleX(1.3)" : "scaleX(1)",
                    boxShadow: isCurrent
                      ? "0 0 8px rgba(139, 69, 208, 0.6)" // Current glow - purple
                      : isPast
                      ? "0 0 3px rgba(139, 69, 208, 0.3)" // Past subtle glow
                      : "none",
                    position: "relative",
                    cursor: "pointer"
                  }}
                  title={`Beat ${beatNumber} at ${formatTime(beat.time)}`}
                >
                  {/* Inner gradient for depth */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: isCurrent
                        ? "linear-gradient(to top, rgba(139, 69, 208, 0.9), rgba(179, 129, 255, 0.7))"
                        : isPast
                        ? "linear-gradient(to top, rgba(139, 69, 208, 0.6), rgba(179, 129, 255, 0.4))"
                        : "linear-gradient(to top, rgba(139, 69, 208, 0.2), rgba(179, 129, 255, 0.1))",
                      borderRadius: "1px",
                    }}
                  />
                  
                  {/* Beat number indicator on top of bar */}
                  {isCurrent && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-18px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(139, 69, 208, 0.9)",
                        color: "white",
                        fontSize: "10px",
                        padding: "1px 4px",
                        borderRadius: "3px",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)"
                      }}
                    >
                      #{beatNumber}
                    </div>
                  )}
                </div>

                {/* Time label below bar */}
                <div
                  className="small"
                  style={{
                    color: isCurrent 
                      ? "rgba(255, 255, 255, 0.95)" 
                      : isPast 
                      ? "rgba(200, 162, 255, 0.8)" 
                      : "rgba(200, 162, 255, 0.5)",
                    fontSize: "9px",
                    marginTop: "2px",
                    textAlign: "center",
                    fontWeight: isCurrent ? "600" : isPast ? "500" : "normal",
                    textShadow: isCurrent ? "0 1px 2px rgba(0, 0, 0, 0.4)" : "none"
                  }}
                >
                  {formatTime(beat.time)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add summary info */}
        <div className="text-center mt-2">
          <small className="text-muted">
            Total beats: {beatsData.length} | Bars: {Math.ceil(beatsData.length / 4)} | Duration:{" "}
            {formatTime(beatsData[beatsData.length - 1]?.time || 0)}
            {isCurrentProject && currentTime !== undefined && (
              <span> | Current position: {formatTime(currentTime)}</span>
            )}
          </small>
        </div>
      </div>
    );
  };

  // Enhanced render chords timeline - show ALL chords
  const renderChordsTimeline = () => {
    if (chordsLoading) {
      return (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <p className="small text-muted mt-2">Loading chords...</p>
        </div>
      );
    }

    if (chordsError) {
      return (
        <Alert variant="warning" className="small">
          {chordsError}
        </Alert>
      );
    }

    if (!chordsData || !Array.isArray(chordsData) || chordsData.length === 0) {
      return (
        <div className="text-center py-3">
          <FontAwesomeIcon icon={faGuitar} className="text-muted mb-2" />
          <p className="small text-muted">No chords data available</p>
        </div>
      );
    }

    // Sort all chords by start time
    const sortedChords = [...chordsData].sort((a, b) => a.start - b.start);

    // Check if this project is currently playing
    const isCurrentProject = String(currentProjectId) === String(project?.id);

    return (
      <div
        className="chords-timeline"
        style={{ overflowX: "auto", padding: "10px 0", height: "170px" }}
      >
        <div
          className="d-flex align-items-center gap-1"
          style={{ minWidth: "max-content", paddingBottom: "10px" }}
        >
          {sortedChords.map((chord, index) => {
            const chordName = getChordDisplayName(chord);
            // Use improved detection logic - match with getCurrentChord logic
            const isCurrent =
              isCurrentProject &&
              currentTime !== undefined &&
              currentTime !== null &&
              chord.start !== undefined &&
              chord.end !== undefined &&
              chord.start !== null &&
              chord.end !== null &&
              currentTime >= chord.start &&
              currentTime <= chord.end;
            const isPast =
              isCurrentProject &&
              currentTime !== undefined &&
              currentTime !== null &&
              chord.end !== undefined &&
              chord.end !== null &&
              chord.end < currentTime;
            const isUpcoming =
              isCurrentProject &&
              currentTime !== undefined &&
              currentTime !== null &&
              chord.start !== undefined &&
              chord.start !== null &&
              chord.start > currentTime;
            const chordColor = getChordColor(chordName);
            const duration = chord.end - chord.start;

            return (
              <div
                key={index}
                className={`chord-marker d-flex flex-column align-items-center ${
                  isCurrent ? "current" : ""
                }`}
                style={{
                  minWidth: Math.max(60, duration * 15), // Width based on duration
                  padding: "8px 4px",
                  borderRadius: "6px",
                  backgroundColor: isCurrent
                    ? `${chordColor}20` // Much darker opacity for current
                    : isPast
                    ? "rgba(34, 139, 34, 0.08)" // Darker green for past
                    : isUpcoming || !isCurrentProject
                    ? "rgba(255, 255, 255, 0.02)" // Very subtle for upcoming
                    : "rgba(255, 255, 255, 0.02)",
                  border: isCurrent
                    ? `1px solid ${chordColor}60` // More subtle border
                    : isPast
                    ? "1px solid rgba(34, 139, 34, 0.2)"
                    : "1px solid rgba(255, 255, 255, 0.05)",
                  transition: "all 0.2s ease",
                  margin: "0 1px",
                  transform: isCurrent ? "scale(1.05)" : "scale(1)", // Less prominent scaling
                  boxShadow: isCurrent ? `0 0 6px ${chordColor}30` : "none", // Softer glow
                  backdropFilter: "blur(10px)", // Glass effect
                  WebkitBackdropFilter: "blur(10px)", // Safari support
                }}
              >
                <Badge
                  style={{
                    backgroundColor: isCurrent
                      ? `${chordColor}60` // More subtle for current
                      : isPast
                      ? "rgba(34, 139, 34, 0.4)"
                      : `${chordColor}40`, // Darker for non-current
                    fontSize: "var(--font-xs)",
                    marginBottom: "4px",
                    fontWeight: "500", // Less bold
                    opacity: isCurrent ? 0.9 : isPast ? 0.7 : 0.5, // More subtle opacity
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(5px)",
                    WebkitBackdropFilter: "blur(5px)"
                  }}
                >
                  {chordName}
                </Badge>
                <div
                  className="small"
                  style={{
                    color: isCurrent ? "rgba(255, 255, 255, 0.9)" : isPast ? "rgba(144, 238, 144, 0.7)" : "rgba(217, 217, 217, 0.6)",
                    fontWeight: isCurrent ? "500" : "normal",
                    fontSize: "var(--font-xs)",
                    textAlign: "center",
                    textShadow: isCurrent ? "0 1px 2px rgba(0, 0, 0, 0.3)" : "none"
                  }}
                >
                  {formatTime(chord.start)}-{formatTime(chord.end)}
                </div>
                <div
                  className="smaller text-muted"
                  style={{
                    fontSize: "var(--font-xs)",
                    color: isCurrent ? "rgba(255, 255, 255, 0.8)" : isPast ? "rgba(144, 238, 144, 0.6)" : "rgba(153, 153, 153, 0.5)",
                  }}
                >
                  {duration.toFixed(1)}s
                </div>
                {chord.start_bar !== undefined && (
                  <div
                    className="smaller text-muted"
                    style={{
                      fontSize: "var(--font-xs)",
                      color: isCurrent ? "rgba(255, 255, 255, 0.7)" : isPast ? "rgba(144, 238, 144, 0.5)" : "rgba(153, 153, 153, 0.4)",
                    }}
                  >
                    {chord.start_bar}.{chord.start_beat}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add summary info */}
        <div className="text-center mt-2">
          <small className="text-muted">
            Total chords: {chordsData.length} | Duration:{" "}
            {formatTime(chordsData[chordsData.length - 1]?.end || 0)}
            {isCurrentProject && currentTime !== undefined && (
              <span> | Current position: {formatTime(currentTime)}</span>
            )}
          </small>
        </div>
      </div>
    );
  };

  // Show loading state while fetching data
  if (!beatsChordAsset) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon
          icon={faMusic}
          className="text-muted mb-2"
          style={{ fontSize: "var(--font-3xl)", opacity: 0.3 }}
        />
        <p className="text-muted">
          No beats and chords data available for this project
        </p>
      </div>
    );
  }

  if ((beatsLoading || chordsLoading) && !beatsData && !chordsData) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2">Loading beats and chords data...</p>
      </div>
    );
  }

  // Check if we have any valid data to show
  const hasValidData =
    (beatsData && beatsData.length > 0) ||
    (chordsData && chordsData.length > 0);

  if (!hasValidData && !beatsLoading && !chordsLoading) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon
          icon={faMusic}
          className="text-muted mb-2"
          style={{ fontSize: "var(--font-3xl)", opacity: 0.3 }}
        />
        <p className="text-muted">
          No valid beats and chords data found for this project
        </p>
        {(beatsError || chordsError) && (
          <Alert variant="warning" className="mt-2">
            {beatsError && <div>Beats: {beatsError}</div>}
            {chordsError && <div>Chords: {chordsError}</div>}
          </Alert>
        )}
      </div>
    );
  }

  // Always show the visualization if we have data, but only highlight if audio is playing
  const isCurrentProject = String(currentProjectId) === String(project?.id);

  return (
    <div className="beats-chord-visualization">
      {/* Metadata */}
      {renderMetadata()}

      {/* Current playback info - only show if this project is playing */}
      {isCurrentProject && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={isPlaying ? faSpinner : faClock}
                spin={isPlaying}
                className="me-2"
                style={{ color: "var(--purple-accent)" }}
              />
              <span style={{ color: "#d9d9d9" }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            {currentBeat && (
              <Badge bg="primary" style={{ fontSize: "var(--font-sm)" }}>
                Bar: {currentBeat.bar || Math.ceil((currentBeat.beatNum || 1) / 4)} 
                {currentBeat.beatNum && ` (Beat #${currentBeat.beatNum})`} 
                (t: {currentBeat.time?.toFixed(2)}s)
              </Badge>
            )}

            {currentChord && (
              <Badge
                style={{
                  backgroundColor: getChordColor(
                    getChordDisplayName(currentChord)
                  ),
                  fontSize: "var(--font-sm)",
                }}
              >
                {getChordDisplayName(currentChord)} (
                {currentChord.start?.toFixed(1)}-{currentChord.end?.toFixed(1)}
                s)
              </Badge>
            )}
          </div>

          {/* Debug info for developers */}
          {process.env.NODE_ENV === "development" && (
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">
                Playing: {isPlaying ? "Yes" : "No"} | Project Match:{" "}
                {String(currentProjectId) === String(project?.id) ? "Yes" : "No"} | Beats:{" "}
                {beatsData?.length || 0} | Chords: {chordsData?.length || 0}
              </small>
            </div>
          )}
        </div>
      )}

      {/* Progress bar - only show if this project is playing */}
      {isCurrentProject && renderProgressBar()}

      {/* Legend - always show */}
      <div className="d-flex justify-content-center gap-4 mb-3">
        <div className="d-flex align-items-center gap-1">
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgba(139, 69, 208, 0.25)",
              border: "1px solid rgba(139, 69, 208, 0.6)",
              borderRadius: "3px",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              boxShadow: "0 0 4px rgba(139, 69, 208, 0.3)"
            }}
          ></div>
          <small className="text-muted">Currently Playing</small>
        </div>
        <div className="d-flex align-items-center gap-1">
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgba(139, 69, 208, 0.12)",
              border: "1px solid rgba(139, 69, 208, 0.3)",
              borderRadius: "3px",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)"
            }}
          ></div>
          <small className="text-muted">Already Played</small>
        </div>
        <div className="d-flex align-items-center gap-1">
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgba(139, 69, 208, 0.04)",
              border: "1px solid rgba(139, 69, 208, 0.1)",
              borderRadius: "3px",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)"
            }}
          ></div>
          <small className="text-muted">Upcoming / Not Playing</small>
        </div>
      </div>

      {/* Status message when not playing */}
      {!isCurrentProject && (beatsData || chordsData) && (
        <div className="text-center py-2 mb-3">
          <small className="text-muted">
            <FontAwesomeIcon icon={faMusic} className="me-2" />
            Play an audio file from this project to see real-time highlighting
          </small>
        </div>
      )}

      {/* Status message when project matches but not playing */}
      {/* {isCurrentProject && !isPlaying && (beatsData || chordsData) && (
        <div className="text-center py-2 mb-3">
          <Badge bg="info" className="me-2">
            <FontAwesomeIcon icon={faMusic} className="me-1" />
            Ready for Highlighting
          </Badge>
          <small className="text-muted">
            Press play to start real-time beat and chord highlighting
            {beatsData?.[0]?.time > 0 && (
              <span> (Music starts at {formatTime(beatsData[0].time)})</span>
            )}
          </small>
        </div>
      )} */}

      {/* Real-time status when playing */}
      {isCurrentProject && isPlaying && (beatsData || chordsData) && (
        <div className="text-center py-2 mb-3">
          <Badge bg="success" className="me-2">
            <FontAwesomeIcon icon={faPlay} className="me-1" />
            Live Highlighting Active
          </Badge>
          {(currentBeat || currentChord) && (
            <Badge bg="primary">Synced with Audio</Badge>
          )}
        </div>
      )}

      {/* Beats section */}
      {(beatsData || beatsLoading) && (
        <Card
          className="mb-3"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Card.Body className="py-3">
            <h6
              className="mb-3"
              style={{
                color: "#d9d9d9",
              }}
            >
              <FontAwesomeIcon
                icon={faMusic}
                className="me-2"
                style={{ color: "var(--purple-accent)" }}
              />
              All Beats Timeline
            </h6>
            {renderBeatsTimeline()}
          </Card.Body>
        </Card>
      )}

      {/* Chords section */}
      {(chordsData || chordsLoading) && (
        <Card
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Card.Body className="py-3">
            <h6 className="mb-3" style={{ color: "#d9d9d9" }}>
              <FontAwesomeIcon
                icon={faGuitar}
                className="me-2"
                style={{ color: "var(--purple-accent)" }}
              />
              All Chords Timeline
            </h6>
            {renderChordsTimeline()}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default BeatsChordVisualization;
